import type { TermSheetExtraSection, TermSheetSectionRow } from "./types";

export function formatTermSheetLabel(label: string): string {
  const trimmed = label.trim();
  return trimmed.endsWith(":") ? trimmed : `${trimmed}:`;
}

/** Default document order: built-in rows only (custom rows are inserted via rowOrder). */
export function createInitialRowOrder(sections: TermSheetSectionRow[]): string[] {
  return sections.map((section) => section.id);
}

/** Builds preview/export rows using the editor's drag-and-drop order. */
export function buildOrderedTermSheetSections(
  sections: TermSheetSectionRow[],
  extraSections: TermSheetExtraSection[],
  rowOrder: string[],
): TermSheetSectionRow[] {
  const builtinById = new Map(sections.map((section) => [section.id, section]));
  const customById = new Map(extraSections.map((section) => [section.id, section]));
  const ordered: TermSheetSectionRow[] = [];

  for (const id of rowOrder) {
    const builtin = builtinById.get(id);
    if (builtin) {
      ordered.push(builtin);
      continue;
    }

    const custom = customById.get(id);
    if (custom) {
      ordered.push({
        id: custom.id,
        label: custom.label,
        content: custom.content,
        included: custom.included,
        isCustom: true,
      });
    }
  }

  return ordered;
}

export function insertRowAfterParent(
  rowOrder: string[],
  parentId: string,
  newRowId: string,
): string[] {
  const parentIndex = rowOrder.indexOf(parentId);
  if (parentIndex === -1) {
    return [...rowOrder, newRowId];
  }

  const next = [...rowOrder];
  next.splice(parentIndex + 1, 0, newRowId);
  return next;
}

export function removeRowFromOrder(rowOrder: string[], rowId: string): string[] {
  return rowOrder.filter((id) => id !== rowId);
}

/** Inserts user-added sections immediately after their parent row. */
export function mergeTermSheetSections(
  sections: TermSheetSectionRow[],
  extraSections: TermSheetExtraSection[],
): TermSheetSectionRow[] {
  const extrasByParent = new Map<string, TermSheetExtraSection[]>();

  for (const extra of extraSections) {
    const existing = extrasByParent.get(extra.afterSectionId) ?? [];
    existing.push(extra);
    extrasByParent.set(extra.afterSectionId, existing);
  }

  const merged: TermSheetSectionRow[] = [];

  for (const section of sections) {
    merged.push(section);

    const extras = extrasByParent.get(section.id) ?? [];
    for (const extra of extras) {
      merged.push({
        id: extra.id,
        label: extra.label,
        content: extra.content,
        included: extra.included,
        isCustom: true,
      });
    }
  }

  return merged;
}

export function getVisibleTermSheetSections(
  sections: TermSheetSectionRow[],
): TermSheetSectionRow[] {
  return sections.filter(
    (section) => section.included && section.content.trim(),
  );
}

export function createExtraSectionId(): string {
  return `custom-${crypto.randomUUID()}`;
}

export function toTermSheetDownloadFilename(dealName: string | null | undefined): string {
  const safeName = (dealName?.trim() || "Deal").replace(/[<>:"/\\|?*]/g, "_");
  const datetime = new Date().toISOString().replace(/[:.]/g, "-");
  return `${safeName}_Indicative_TermSheet_${datetime}.docx`;
}
